import React, { useState, useEffect } from 'react'
import { useTheme } from '../../hooks/useTheme'

interface SpaceLocationMapProps {
  espacio: {
    id: string
    nombre: string
    codigo?: string
    plano_x?: number
    plano_y?: number
    plano_radio?: number
    plano_vertices?: Array<{ x: number; y: number }>
  } | null | undefined
  showAnimations?: boolean  // false = más sutil, true = como QR
  showContext?: boolean     // Mostrar espacios vecinos en gris (future feature)
  className?: string
}

const SpaceLocationMap: React.FC<SpaceLocationMapProps> = ({
  espacio,
  showAnimations = false,
  showContext = false,
  className = ''
}) => {
  const { colors } = useTheme()
  const [isZoomOpen, setIsZoomOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const planoImageUrl = `${import.meta.env.VITE_API_URL}/static/uploads/planos/plano-camping.png`

  // Detectar tamaño de pantalla
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Cerrar zoom con ESC
  useEffect(() => {
    if (!isZoomOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setIsZoomOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isZoomOpen])

  if (!espacio) {
    return (
      <div className={`bg-gray-100 rounded-lg p-8 text-center ${className}`}>
        <p className="text-gray-500">No hay información de ubicación disponible</p>
      </div>
    )
  }

  const tieneCoordenadas = (
    (espacio.plano_x !== undefined && espacio.plano_y !== undefined) ||
    (espacio.plano_vertices !== undefined && espacio.plano_vertices.length >= 3)
  )

  if (!tieneCoordenadas) {
    return (
      <div className={`bg-gray-100 rounded-lg p-8 text-center ${className}`}>
        <div className="text-4xl mb-2">📍</div>
        <p className="text-gray-600 font-medium">Espacio: {espacio.nombre}</p>
        <p className="text-sm text-gray-500 mt-1">Ubicación no configurada en el plano</p>
      </div>
    )
  }

  // Calcular el centro del espacio para el zoom
  let centerX = 0
  let centerY = 0

  if (espacio.plano_vertices && espacio.plano_vertices.length >= 3) {
    // Calcular centroide del polígono
    const sumX = espacio.plano_vertices.reduce((acc, v) => acc + v.x, 0)
    const sumY = espacio.plano_vertices.reduce((acc, v) => acc + v.y, 0)
    centerX = sumX / espacio.plano_vertices.length
    centerY = sumY / espacio.plano_vertices.length
  } else if (espacio.plano_x !== undefined && espacio.plano_y !== undefined) {
    // Usar coordenadas directas
    centerX = espacio.plano_x
    centerY = espacio.plano_y
  }

  // Convertir coordenadas del SVG (1920x1080) a porcentajes
  const centerXPercent = (centerX / 1920) * 100
  const centerYPercent = (centerY / 1080) * 100

  const renderPlanoContent = () => (
    <>
      {/* Imagen de fondo del plano */}
      <img
        src={planoImageUrl}
        alt="Plano del camping"
        className="w-full h-full object-cover"
        style={{ aspectRatio: '16/9' }}
      />

      {/* SVG overlay con espacio destacado */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Si tiene plano_vertices: dibujar polígono exacto */}
        {espacio.plano_vertices && espacio.plano_vertices.length >= 3 && (
          <>
            <polygon
              points={espacio.plano_vertices.map(v => `${v.x},${v.y}`).join(' ')}
              fill="rgba(34, 197, 94, 0.2)"
              stroke="#22c55e"
              strokeWidth="3"
              style={{
                filter: 'drop-shadow(0 0 8px rgba(34, 197, 94, 0.6))'
              }}
            />

            {/* Mira/Crosshair animada en el centroide (si showAnimations = true) */}
            {showAnimations && (() => {
              const sumX = espacio.plano_vertices.reduce((acc, v) => acc + v.x, 0)
              const sumY = espacio.plano_vertices.reduce((acc, v) => acc + v.y, 0)
              const cx = sumX / espacio.plano_vertices.length
              const cy = sumY / espacio.plano_vertices.length

              return (
                <g>
                  <style>{`
                    @keyframes zoomIn {
                      0% { r: 200px; opacity: 0; }
                      50% { opacity: 1; }
                      100% { r: 60px; opacity: 0; }
                    }
                    @keyframes zoomIn2 {
                      0% { r: 150px; opacity: 0; }
                      50% { opacity: 1; }
                      100% { r: 40px; opacity: 0; }
                    }
                    .crosshair-ring1 { animation: zoomIn 2.5s infinite; }
                    .crosshair-ring2 { animation: zoomIn2 2.5s infinite 0.5s; }
                  `}</style>

                  {/* Anillos concéntricos que se cierran */}
                  <circle cx={cx} cy={cy} r="200" fill="none" stroke="#ff0000" strokeWidth="2" className="crosshair-ring1" />
                  <circle cx={cx} cy={cy} r="150" fill="none" stroke="#ff0000" strokeWidth="2" className="crosshair-ring2" />

                  {/* Círculo central pequeño */}
                  <circle cx={cx} cy={cy} r="15" fill="none" stroke="#ff0000" strokeWidth="2" />

                  {/* Cruz - Líneas horizontales */}
                  <line x1={cx - 60} y1={cy} x2={cx - 30} y2={cy} stroke="#ff0000" strokeWidth="2" />
                  <line x1={cx + 30} y1={cy} x2={cx + 60} y2={cy} stroke="#ff0000" strokeWidth="2" />

                  {/* Cruz - Líneas verticales */}
                  <line x1={cx} y1={cy - 60} x2={cx} y2={cy - 30} stroke="#ff0000" strokeWidth="2" />
                  <line x1={cx} y1={cy + 30} x2={cx} y2={cy + 60} stroke="#ff0000" strokeWidth="2" />

                  {/* Punto central */}
                  <circle cx={cx} cy={cy} r="3" fill="#ff0000" />
                </g>
              )
            })()}
          </>
        )}

        {/* Si tiene plano_x/plano_y (pero NO tiene plano_vertices): dibujar círculo o mira */}
        {espacio.plano_x !== undefined && espacio.plano_y !== undefined &&
         !(espacio.plano_vertices && espacio.plano_vertices.length >= 3) && (
          <>
            {/* Círculo principal (solo si NO hay animaciones) */}
            {!showAnimations && (
              <circle
                cx={espacio.plano_x}
                cy={espacio.plano_y}
                r={espacio.plano_radio || 30}
                fill={`rgba(${parseInt(colors.primary.slice(1, 3), 16)}, ${parseInt(colors.primary.slice(3, 5), 16)}, ${parseInt(colors.primary.slice(5, 7), 16)}, 0.3)`}
                stroke={colors.primary}
                strokeWidth="4"
                style={{
                  filter: `drop-shadow(0 0 8px ${colors.primary}80)`
                }}
              />
            )}

            {/* Mira completa (solo si showAnimations = true) */}
            {showAnimations && (
              <>
                <style>{`
                  @keyframes pulse-glow {
                    0%, 100% { transform: scale(1); opacity: 0.8; }
                    50% { transform: scale(1.15); opacity: 0.4; }
                  }
                  @keyframes zoomIn-circle {
                    0% { r: 200px; opacity: 0; }
                    50% { opacity: 1; }
                    100% { r: 60px; opacity: 0; }
                  }
                  @keyframes zoomIn2-circle {
                    0% { r: 150px; opacity: 0; }
                    50% { opacity: 1; }
                    100% { r: 40px; opacity: 0; }
                  }
                  .pulse-glow-animation {
                    animation: pulse-glow 2s infinite;
                  }
                  .crosshair-ring1-circle { animation: zoomIn-circle 2.5s infinite; }
                  .crosshair-ring2-circle { animation: zoomIn2-circle 2.5s infinite 0.5s; }
                `}</style>

                {/* Anillos concéntricos que se cierran (mira tipo sniper) */}
                <circle cx={espacio.plano_x} cy={espacio.plano_y} r="200" fill="none" stroke="#ff0000" strokeWidth="2" className="crosshair-ring1-circle" />
                <circle cx={espacio.plano_x} cy={espacio.plano_y} r="150" fill="none" stroke="#ff0000" strokeWidth="2" className="crosshair-ring2-circle" />

                {/* Círculo central pequeño de la mira */}
                <circle cx={espacio.plano_x} cy={espacio.plano_y} r="15" fill="none" stroke="#ff0000" strokeWidth="2" />

                {/* Cruz - Líneas horizontales */}
                <line x1={espacio.plano_x - 60} y1={espacio.plano_y} x2={espacio.plano_x - 30} y2={espacio.plano_y} stroke="#ff0000" strokeWidth="2" />
                <line x1={espacio.plano_x + 30} y1={espacio.plano_y} x2={espacio.plano_x + 60} y2={espacio.plano_y} stroke="#ff0000" strokeWidth="2" />

                {/* Cruz - Líneas verticales */}
                <line x1={espacio.plano_x} y1={espacio.plano_y - 60} x2={espacio.plano_x} y2={espacio.plano_y - 30} stroke="#ff0000" strokeWidth="2" />
                <line x1={espacio.plano_x} y1={espacio.plano_y + 30} x2={espacio.plano_x} y2={espacio.plano_y + 60} stroke="#ff0000" strokeWidth="2" />

                {/* Punto central */}
                <circle cx={espacio.plano_x} cy={espacio.plano_y} r="3" fill="#ff0000" />

                {/* Círculo pulsador grande */}
                <circle
                  cx={espacio.plano_x}
                  cy={espacio.plano_y}
                  r={(espacio.plano_radio || 30) + 40}
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="4"
                  className="pulse-glow-animation"
                  style={{
                    filter: 'drop-shadow(0 0 12px rgba(239, 68, 68, 0.8))'
                  }}
                />

                {/* Círculo interior punteado */}
                <circle
                  cx={espacio.plano_x}
                  cy={espacio.plano_y}
                  r={(espacio.plano_radio || 30) + 25}
                  fill="none"
                  stroke="#f87171"
                  strokeWidth="2"
                  strokeDasharray="6,4"
                  style={{ opacity: 0.7 }}
                />
              </>
            )}
          </>
        )}
      </svg>


      {/* Hint text en la esquina */}
      <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
        📍 Click para hacer zoom
      </div>
    </>
  )

  return (
    <>
      {/* Vista normal - clickeable para zoom */}
      <div
        className={`relative w-full bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow ${className}`}
        onClick={() => setIsZoomOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsZoomOpen(true)
          }
        }}
        title="Click para hacer zoom"
      >
        {renderPlanoContent()}
      </div>

      {/* Modal de Zoom */}
      {isZoomOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIsZoomOpen(false)}
        >
          <div
            className="relative w-full h-full max-w-7xl max-h-[90vh] bg-gray-900 rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Contenedor con viewport centrado en el espacio */}
            <div
              className="relative w-full h-full"
              style={{
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  width: '1920px',
                  height: '1080px',
                  // Centrar el espacio en el viewport
                  left: '50%',
                  top: '50%',
                  transform: `translate(-${centerXPercent}%, -${centerYPercent}%) scale(1)`,
                  transformOrigin: `${centerXPercent}% ${centerYPercent}%`,
                }}
              >
                {renderPlanoContent()}
              </div>
            </div>

            {/* Botón cerrar */}
            <button
              onClick={() => setIsZoomOpen(false)}
              className="fixed top-8 right-8 w-12 h-12 bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-colors z-10 text-xl"
              aria-label="Cerrar zoom"
            >
              ✕
            </button>

            {/* Texto de ayuda */}
            <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full">
              Presiona ESC o click afuera para cerrar
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SpaceLocationMap
